import { useState } from "react";
import { GripVertical } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { MATCH_STATUS_LABEL, STAGE_LABEL } from "@/lib/tournaments";

const STATUS_CLS = {
  scheduled: "bg-slate-100 text-slate-700",
  in_progress: "bg-ocean text-white",
  completed: "bg-emerald-100 text-emerald-800",
  postponed: "bg-amber-100 text-amber-800",
  cancelled: "bg-red-100 text-red-700",
};

export default function MatchList({ matches, renderActions, onReorder }) {
  const stages = ["group", "semifinal", "third_place", "final"].filter((s) => matches.some((m) => m.stage === s));
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);

  const drop = (stage, targetId) => {
    if (!drag || drag.stage !== stage || drag.id === targetId) { setDrag(null); setOver(null); return; }
    const ids = matches.filter((m) => m.stage === stage).map((m) => m.id);
    const from = ids.indexOf(drag.id), to = ids.indexOf(targetId);
    ids.splice(from, 1); ids.splice(to, 0, drag.id);
    setDrag(null); setOver(null);
    onReorder?.(stage, ids);
  };

  return (
    <div className="space-y-8" data-testid="match-list">
      {stages.map((stage) => (
        <div key={stage}>
          <h3 className="text-[11px] font-black uppercase tracking-[0.25em] text-sunset mb-3">{STAGE_LABEL[stage]}</h3>
          <div className="space-y-2">
            {matches.filter((m) => m.stage === stage).map((m) => (
              <MatchRow key={m.id} m={m} actions={renderActions?.(m)} draggable={!!onReorder}
                dragging={drag?.id === m.id} isOver={over === m.id && drag?.id !== m.id}
                onDragStart={() => setDrag({ id: m.id, stage })}
                onDragOver={(e) => { if (drag?.stage === stage) { e.preventDefault(); setOver(m.id); } }}
                onDragLeave={() => setOver((o) => (o === m.id ? null : o))}
                onDrop={(e) => { e.preventDefault(); drop(stage, m.id); }}
                onDragEnd={() => { setDrag(null); setOver(null); }} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function MatchRow({ m, actions, draggable, dragging, isOver, ...dnd }) {
  const win = m.result?.winner;
  const nameCls = (side) => `font-bold ${win === side ? "text-slate-900" : win ? "text-slate-400" : "text-slate-800"}`;
  return (
    <div draggable={draggable} {...(draggable ? dnd : {})}
      className={`rounded-2xl border bg-slate-50/60 px-4 py-3 grid ${draggable ? "grid-cols-[auto_auto_1fr_auto]" : "grid-cols-[auto_1fr_auto]"} gap-3 items-center transition-all ${dragging ? "opacity-40 scale-[0.99]" : ""} ${isOver ? "border-ocean ring-2 ring-ocean/30 translate-y-0.5" : "border-slate-100"}`}
      data-testid={`match-${m.number}`} data-match-id={m.id}>
      {draggable && <div className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-ocean" title="Σύρε για αλλαγή σειράς" data-testid={`match-${m.number}-drag`}><GripVertical className="h-5 w-5" /></div>}
      <div className="text-center w-14">
        <div className="font-heading font-black text-ocean text-lg leading-none">#{m.number}</div>
        <div className="text-[10px] text-slate-500 mt-1">{m.scheduled_time || "—"}</div>
        <div className="text-[10px] text-slate-500">Γήπ. {m.court || "—"}</div>
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{m.round_label}</div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          <span className={nameCls("a")} data-testid={`match-${m.number}-team-a`}>{m.team_a_name || m.team_a_label || "TBD"}</span>
          <span className="text-slate-400 text-xs">vs</span>
          <span className={nameCls("b")} data-testid={`match-${m.number}-team-b`}>{m.team_b_name || m.team_b_label || "TBD"}</span>
        </div>
        {m.result && (
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm" data-testid={`match-${m.number}-score`}>
            <span className="font-heading font-black text-slate-900">{m.result.sets_a}–{m.result.sets_b}</span>
            <span className="text-slate-500 text-xs">({m.sets.map(([a, b]) => `${a}–${b}`).join(", ")})</span>
            {m.outcome !== "normal" && <span className="text-[10px] uppercase tracking-widest text-amber-700">{m.outcome.startsWith("forfeit") ? "άνευ αγώνος" : "αποχώρηση"}</span>}
          </div>
        )}
      </div>
      <div className="flex flex-col items-end gap-2">
        <Badge className={`${STATUS_CLS[m.status]} text-[10px]`} data-testid={`match-${m.number}-status`}>{MATCH_STATUS_LABEL[m.status]}</Badge>
        {actions}
      </div>
    </div>
  );
}
